import { ReactNode, createContext, useEffect, useState } from 'react';
import { ConnectivityNotifier } from '@shared/scenes/connection/connectivity';
import { usePrevious } from '@shared/util/hooks';

const MAX_BACKOFF = 30000;

export type MBConnectionStatus = 'online' | 'offline' | 'unknown';
export const ConnectionContext = createContext<MBConnectionStatus>('unknown');

interface Props {
  children: ReactNode;
  notifier: ConnectivityNotifier;
}

export function ConnectionProvider({ children, notifier }: Props) {
  const [status, setStatus] = useState<MBConnectionStatus>('unknown');

  useEffect(() => {
    testConnectivity(setStatus);
    const offline = (reason: string) => {
      console.debug(`GOING OFFLINE (reason = ${reason})`);
      setStatus('offline');
    };
    notifier.addEventListener('offline', offline);

    const online = (reason: string) => {
      console.debug(`TESTING ONLINE (reason = ${reason})`);
      testConnectivity(setStatus);
    };
    notifier.addEventListener('online', online);

    return () => {
      notifier.removeEventListener('offline', offline);
      notifier.removeEventListener('online', online);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevStatus = usePrevious(status);
  useEffect(() => {
    if (prevStatus === 'online' && status === 'offline') {
      testConnectivity(setStatus);
    }
  }, [prevStatus, status]);

  return <ConnectionContext.Provider value={status}>{children}</ConnectionContext.Provider>;
}

async function testConnectivity(
  setStatus: (status: MBConnectionStatus) => void,
  backoff: number = 1000,
) {
  console.debug(`testConnectivity [backoff = ${backoff}]`);
  try {
    await healthCheck();
    setStatus('online');
  } catch (error) {
    setStatus('offline');
    setTimeout(() => {
      const newBackoff = backoff * 1.2;
      testConnectivity(setStatus, Math.min(MAX_BACKOFF, newBackoff));
    }, backoff);
  }
}

async function healthCheck(timeout: number = 5000) {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, timeout);
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/healthz`, {
      signal: controller.signal,
    });
    if (response.status !== 200) {
      throw new Error('Health check failed');
    }
  } catch (error) {
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
